<div class="text-fg">
  @if (loading()) {
    <div class="flex items-center justify-center gap-3 p-12 text-sm text-muted">
      <ui-spinner />
      <span>Loading bank account…</span>
    </div>
  } @else if (error()) {
    <div class="p-8 text-center text-danger-fg">
      <p class="mb-3">{{ error() }}</p>
      <button type="button" uiButton variant="outline" size="sm" (click)="load()">Retry</button>
    </div>
  } @else if (hub(); as h) {
    <header class="mb-5">
      <button type="button" class="mb-2 inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-sm text-muted hover:text-fg" (click)="goBack()">
        <ng-icon name="lucideArrowLeft" size="16" />
        Back
      </button>
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 class="m-0 flex items-center gap-2 text-[1.4rem] font-bold text-fg">
            <ng-icon name="lucideLandmark" size="22" />
            {{ str(account()['account_name']) !== '—' ? str(account()['account_name']) : str(account()['merchant_reference']) }}
            <span class="rounded-full px-2.5 py-1 text-[0.8125rem] font-semibold" [class]="statusClass(statusValue(account()['status']))">{{ cell(account()['status']) }}</span>
          </h1>
          @if (!h['has_account_number']) {
            <p class="mb-0 mt-1 text-sm text-warning-fg">The provider has not issued an account number yet, so this account cannot be used for pull, push or transfers.</p>
          }
        </div>
        <button type="button" uiButton variant="outline" size="sm" (click)="load()" title="Refresh">
          <ng-icon name="lucideRefreshCw" size="16" />
        </button>
      </div>
    </header>

    <div class="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
      <section class="rounded-xl border border-border bg-card p-4">
        <h3 class="mb-3 text-base font-semibold text-fg">Account</h3>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          @for (f of accountFields; track f.key) {
            <div class="flex min-w-0 flex-col">
              <span class="mb-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-muted">{{ f.label }}</span>
              <span class="break-words text-sm">{{ str(account()[f.key]) }}</span>
            </div>
          }
        </div>
      </section>
      <section class="rounded-xl border border-border bg-card p-4">
        <h3 class="mb-3 text-base font-semibold text-fg">References & ownership</h3>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          @for (f of referenceFields; track f.key) {
            <div class="flex min-w-0 flex-col">
              <span class="mb-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-muted">{{ f.label }}</span>
              <span class="break-words text-sm">{{ str(account()[f.key]) }}</span>
            </div>
          }
        </div>
        <div class="mt-3 flex flex-wrap gap-2 text-sm">
          @if (account()['merchant_id']) {
            <a class="text-primary hover:underline" [routerLink]="link('merchants', account()['merchant_id'])">Open merchant</a>
          }
          @if (account()['application_id']) {
            <a class="text-primary hover:underline" [routerLink]="link('merchant-applications', account()['application_id'])">Open application</a>
          }
        </div>
      </section>
    </div>

    @for (block of [
      { key: 'statements', title: 'Bank statements', entity: 'bank-statements' },
      { key: 'transfers', title: 'Account transfers', entity: 'account-transfers' },
      { key: 'pulls', title: 'Bank pulls', entity: 'bank-pulls' },
      { key: 'pushes', title: 'Bank pushes', entity: 'bank-pushes' }
    ]; track block.key) {
      <section class="mb-4 rounded-xl border border-border bg-card p-4">
        <h3 class="mb-3 text-base font-semibold text-fg">{{ block.title }}</h3>
        @if (rows($any(block.key)).length) {
          <div uiTableWrap>
            <table uiTable>
              <thead>
                <tr>
                  <th>Reference / type</th>
                  <th>Details</th>
                  <th class="text-right">Amount</th>
                  <th>Status</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                @for (r of rows($any(block.key)); track r['id']) {
                  <tr>
                    <td>
                      <a class="text-primary hover:underline" [routerLink]="link(block.entity, r['id'])">{{ str(r['internal_reference'] ?? r['entry_serial_number'] ?? r['bank_transaction_type'] ?? r['id']) }}</a>
                      @if (r['merchant_reference']) {
                        <div class="text-xs text-muted">{{ r['merchant_reference'] }}</div>
                      }
                    </td>
                    <td class="text-sm text-fg-secondary">{{ str(r['narration'] ?? (r['source_account_number'] ? r['source_account_number'] + ' → ' + r['destination_account_number'] : r['bank_transaction_type'])) }}</td>
                    <td class="text-right font-mono">{{ str(r['amount']) }}</td>
                    <td><span class="rounded-full px-2.5 py-1 text-xs font-semibold" [class]="statusClass(str(r['status']))">{{ str(r['status']) }}</span></td>
                    <td class="text-sm text-muted">{{ str(r['created_at']) }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else {
          <p class="mb-0 text-sm text-muted">Nothing recorded yet.</p>
        }
      </section>
    }

    @if (metadataJson()) {
      <section class="mb-4 rounded-xl border border-border bg-card p-4">
        <h3 class="mb-2 text-base font-semibold text-fg">Metadata</h3>
        <pre class="m-0 max-h-[280px] overflow-auto rounded-md border border-border bg-surface-muted p-3 text-[0.8125rem] text-fg">{{ metadataJson() }}</pre>
      </section>
    }
  }
</div>
